import { useEffect, useRef } from 'react';
import {
  getOrCreateExtensionDeviceId,
  isExtensionAutoAuthEnabled,
} from '../config/extensionTokensFlags';
import { getExtensionSession } from '../services/extensionTokens.api';
import {
  getExtensionId,
  getExtensionStatus,
  sendIngestTokenToExtension,
} from '../services/extensionHandshake';

/**
 * Treat a token as healthy until it is actually dead. Rotation belongs to the
 * extension's own refresh alarm (AD-EA-2, single rotation authority) — the
 * website must not preempt it, or an in-flight refresh can 401 and disconnect
 * a working extension.
 */
export const EXPIRY_SKEW_MS = 30 * 1000;

/**
 * Decide what the website should do about the extension's session.
 * Pure — the whole point is that "skip" is the common answer.
 *
 * @param {{ ok: boolean, connected: boolean, expiresAt: string|null }} status
 * @returns {{ action: 'skip'|'provision', reason: string }}
 */
export function planExtensionAutoConnect(status) {
  if (!status?.ok) {
    // No extension installed, or it never answered — nothing to provision.
    return { action: 'skip', reason: status?.reason || 'extension_unreachable' };
  }
  if (!status.connected) {
    return { action: 'provision', reason: 'not_connected' };
  }
  const expiresAtMs = status.expiresAt ? Date.parse(status.expiresAt) : NaN;
  if (!Number.isFinite(expiresAtMs)) {
    return { action: 'provision', reason: 'unknown_expiry' };
  }
  if (expiresAtMs <= Date.now() + EXPIRY_SKEW_MS) {
    return { action: 'provision', reason: 'expired' };
  }
  return { action: 'skip', reason: 'healthy' };
}

/**
 * Hand the extension an Extension Token without the student doing anything.
 *
 * The website holds the session; the extension holds only its Extension Token
 * and never sees a website JWT (AD-EA-1). Silent on every failure path — this
 * runs for students who may not have the extension installed at all.
 *
 * @param {{ enabled: boolean, onProvisioned?: () => void }} params
 */
export default function useExtensionAutoConnect({ enabled, onProvisioned }) {
  const attemptedRef = useRef(false);
  // Kept in a ref so a changing callback identity cannot re-run the effect and
  // re-trigger provisioning.
  const onProvisionedRef = useRef(onProvisioned);
  onProvisionedRef.current = onProvisioned;

  useEffect(() => {
    // Reset on sign-out so a subsequent sign-in re-provisions in the same tab.
    if (!enabled) {
      attemptedRef.current = false;
      return undefined;
    }
    if (attemptedRef.current) return undefined;
    if (!isExtensionAutoAuthEnabled()) return undefined;
    if (!getExtensionId()) {
      // Misconfiguration, not a user error: the manual panel still works.
      console.warn('[stacktrack] auto-connect disabled — REACT_APP_EXTENSION_ID is not set');
      return undefined;
    }

    attemptedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const status = await getExtensionStatus();
        if (cancelled) return;

        const plan = planExtensionAutoConnect(status);
        if (plan.action === 'skip') return;

        // AD-EA-3: the extension's device id wins; the localStorage id is only
        // a seed for a fresh install that has never held a token.
        const deviceId = status.deviceId || getOrCreateExtensionDeviceId();
        const data = await getExtensionSession({
          deviceId,
          jti: plan.reason === 'expired' ? null : status.jti,
          tokenVersion: status.tokenVersion,
        });
        if (cancelled || data?.reused || !data?.token) return;

        const push = await sendIngestTokenToExtension({
          token: data.token,
          expiresAt: data.expiresAt,
          deviceId: data.deviceId,
          jti: data.jti,
        });

        // A newly provisioned extension has no feature configuration yet.
        if (push?.ok && !cancelled) onProvisionedRef.current?.();
      } catch (err) {
        // 401 (website session gone), 404 (flag off), offline — all benign here.
        console.debug('[stacktrack] extension auto-connect skipped', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
